import type { BillingProductKey, PlanKind } from '../../supabase/functions/_shared/billingPolicy';

// The purchase lane's contract — the ONE shape every purchase implementation answers in, exactly
// like src/native/mediaContract.ts does for the camera. Three implementations exist:
//   src/native/revenueCatAdapter.ts   the real iOS lane (App Store sheet through RevenueCat)
//   src/native/fakePurchases.ts       tests and the browser dev server (no store, no money)
//   (unavailable)                     any other host: says so plainly instead of pretending
//
// Purchases happen in Apple's own sheet. This app never sees a card number, never asks for one and
// never unlocks paid content with a code of its own (App Store Review 3.1.1; Owner decision
// 2026-09-11 X-5: the promo-code entry opens Apple's official redemption sheet).
export type PurchaseFailureCode = 'notConfigured' | 'notNative' | 'network' | 'productUnavailable' | 'notAllowed' | 'identity' | 'unknown';

export interface StoreEntitlement {
  plan: PlanKind | null;
  // Epoch ms the current period ends at (grace period included when the store granted one).
  activeUntil: number | null;
  willRenew: boolean | null;
  // The store's own subscription-management page for this account.
  managementUrl: string | null;
  // Store transaction counts of the two one-time products. The SERVER is the authority on how many
  // are still unspent; these only let the app react immediately after a purchase.
  singlePurchases: number;
  addOnPurchases: number;
  // Who the store thinks this is (must equal the signed-in user id before anything is bought).
  appUserId: string | null;
}

export function emptyStoreEntitlement(): StoreEntitlement {
  return { plan: null, activeUntil: null, willRenew: null, managementUrl: null, singlePurchases: 0, addOnPurchases: 0, appUserId: null };
}

export type StorePurchaseOutcome =
  | { kind: 'purchased'; entitlement: StoreEntitlement }
  // The inspector backed out of Apple's sheet. A neutral outcome, never an error.
  | { kind: 'cancelled' }
  // Ask to Buy / Strong Customer Authentication: Apple will finish it later.
  | { kind: 'pending' }
  | { kind: 'failed'; code: PurchaseFailureCode; detail: string };

export type StoreIdentityOutcome = { ok: true; entitlement: StoreEntitlement } | { ok: false; code: PurchaseFailureCode; detail: string };

export interface PurchasesAdapter {
  // 'ios' = the real store (iPhone and Android) · 'web' = RevenueCat Web Billing on the web version (item 39)
  // · 'fake' = the development simulation · 'unavailable' = no purchases here.
  readonly platform: 'ios' | 'web' | 'fake' | 'unavailable';
  // Whether this build can actually transact (the store SDK key is present on this platform).
  configured(): boolean;
  // Binds the store account to the signed-in Supabase user id and returns what it holds. The app
  // NEVER transacts as an anonymous store user: an anonymous id can hold and restore an
  // entitlement, which is how a purchase lands on the wrong account (memory yanwushi-auth-gate).
  identify(appUserId: string): Promise<StoreIdentityOutcome>;
  entitlement(): Promise<StoreEntitlement | null>;
  purchase(product: BillingProductKey): Promise<StorePurchaseOutcome>;
  restore(): Promise<StoreIdentityOutcome>;
  // Apple's official offer-code redemption sheet (X-5).
  presentOfferCodeSheet(): Promise<{ ok: true } | { ok: false; code: PurchaseFailureCode; detail: string }>;
  // Opens the system subscription-management page. Returns false when the host cannot open it.
  openManageSubscriptions(url: string | null): boolean;
  // Store-side changes that arrive without a purchase call: a redeemed offer code, a renewal, an
  // Ask-to-Buy approval. Returns an unsubscribe function.
  onEntitlementChange(listener: (entitlement: StoreEntitlement) => void): () => void;
}
